import { supabase } from "@/lib/supabase";
import { productCostSheet, WORKFLOW_LABELS } from "@/lib/costSheetPending";
import { analyzeDuplicateCostSheet, canPreserveDuplicateReference, productCodeWithSeason } from "@/lib/manufacturingDuplicate";

const emptyResult = { data: [], error: null };
export const LEGACY_SALE_PRICE_FIELDS = ["precio_v", "precio_v1", "precio_v2", "precio_v3", "precio_v4", "precio_v5"];
export const FAMILY_TARIFF_OPTIONS = [1, 2, 3, 4, 5, 6];

function toFamilyNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toFamilyTariff(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || !FAMILY_TARIFF_OPTIONS.includes(parsed)) return null;
  return parsed;
}

const MFG_PRODUCT_SELECT = "id, sales_product_id, code, description, cod_fam, family:familia!mfg_products_cod_fam_fkey(cod_fam, descrip, tarifa, magnet_label_color), temporada, season:mfc_temporada!mfg_products_temporada_fkey(cod_tem, descrip), composicion, garment_type_id, garment_type:mfg_garment_types(id, name), producto_estrella, observaciones, codigo_patron, ubicacion_patron, sizes, colors, status, sync_to_sales, last_synced_at, images, main_image_index, precio_v, precio_v1, precio_v2, precio_v3, precio_v4, precio_v5, created_at, updated_at";
const TEMPLATE_SELECT =
  "*, family:familia!mfg_cost_sheet_templates_cod_fam_fkey(cod_fam, descrip), lines:mfg_template_lines(*, process:mfg_manufacturing_processes(id, legacy_code, description), material:mfg_materials(id, code, internal_description, default_unit), material_supplier:mfg_material_suppliers(*, supplier:mfg_suppliers(id, erp_code, name)))";

async function runQuery(query, fallback = []) {
  try {
    const { data, error } = await query;
    if (error) throw error;
    return { data: data || fallback, error: null };
  } catch (error) {
    console.error("Manufacturing API error:", error);
    return { data: fallback, error };
  }
}

export const formatMoney = (value) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(value || 0));

export const calculateMaterialLineCost = ({ quantity = 0, waste_pct = 0, unit_cost = 0 }) =>
  Number((Number(quantity || 0) * (1 + Number(waste_pct || 0) / 100) * Number(unit_cost || 0)).toFixed(4));

export const calculateProcessLineCost = ({ quantity = 0, unit_cost = 0 }) =>
  Number((Number(quantity || 0) * Number(unit_cost || 0)).toFixed(4));

export const calculateProcessTotal = (processLine = {}) => {
  const ownCost = Number(processLine.line_cost ?? calculateProcessLineCost(processLine));
  const materialTotal = (processLine.material_lines || []).reduce(
    (sum, line) => sum + Number(line.line_cost ?? calculateMaterialLineCost(line)),
    0
  );
  return Number((ownCost + materialTotal).toFixed(4));
};

export const calculateSheetTotals = (sheet = {}) => {
  const processOwnTotal = (sheet.process_lines || []).reduce(
    (sum, line) => sum + Number(line.line_cost ?? calculateProcessLineCost(line)),
    0
  );
  const materialTotal = (sheet.material_lines || []).reduce(
    (sum, line) => sum + Number(line.line_cost ?? calculateMaterialLineCost(line)),
    0
  );
  return {
    processOwnTotal: Number(processOwnTotal.toFixed(4)),
    materialTotal: Number(materialTotal.toFixed(4)),
    total: Number((processOwnTotal + materialTotal).toFixed(4)),
  };
};

export const SALE_PRICE_MISMATCH_TOLERANCE = 0.01;

function normalizeFamilyCode(codFam) {
  return codFam?.trim()?.toUpperCase() || null;
}

export function familyTariffParams(family = {}) {
  return {
    valor_fijo: toFamilyNumber(family.valor_fijo),
    incremento: toFamilyNumber(family.incremento),
    condicion: toFamilyNumber(family.condicion),
  };
}

export function familyTariffParamsEqual(left, right) {
  const a = familyTariffParams(left);
  const b = familyTariffParams(right);
  return a.valor_fijo === b.valor_fijo && a.incremento === b.incremento && a.condicion === b.condicion;
}

export function calculateTariffPrice(cost, rule = {}) {
  const costNum = toFamilyNumber(cost);
  const fixed = toFamilyNumber(rule.valor_fijo);
  const pct = toFamilyNumber(rule.incremento);
  const minMargin = toFamilyNumber(rule.condicion);
  const rate = (costNum + fixed) * (1 + pct / 100);
  const markup = rate - costNum;
  const applied = markup <= minMargin ? costNum + minMargin : rate;
  return Math.max(0, Number(applied.toFixed(2)));
}

export function calculateFamilySalePrices({ cost = 0, tarifa, valor_fijo = 0, incremento = 0, condicion = 0 } = {}) {
  const prices = Object.fromEntries(LEGACY_SALE_PRICE_FIELDS.map((field) => [field, 0]));
  const tariff = toFamilyTariff(tarifa);
  if (!tariff) return prices;
  prices[LEGACY_SALE_PRICE_FIELDS[tariff - 1]] = calculateTariffPrice(cost, { valor_fijo, incremento, condicion });
  return prices;
}

export function buildTariffRuleMap(families = [], preferredCodFam = null) {
  const preferred = normalizeFamilyCode(preferredCodFam);
  const groups = new Map();
  for (const family of families) {
    const tarifa = toFamilyTariff(family?.tarifa);
    if (!tarifa) continue;
    const list = groups.get(tarifa) || [];
    list.push(family);
    groups.set(tarifa, list);
  }

  const map = {};
  for (const tarifa of FAMILY_TARIFF_OPTIONS) {
    const list = (groups.get(tarifa) || []).slice().sort((left, right) =>
      String(left.cod_fam || "").localeCompare(String(right.cod_fam || ""))
    );
    if (!list.length) {
      map[tarifa] = null;
      continue;
    }
    const conflict = list.some((family) => !familyTariffParamsEqual(family, list[0]));
    const preferredFamily = preferred
      ? list.find((family) => normalizeFamilyCode(family.cod_fam) === preferred)
      : null;
    const chosen = preferredFamily || list[0];
    map[tarifa] = {
      tarifa,
      ...familyTariffParams(chosen),
      conflict,
      families: list,
      sourceCodFam: chosen.cod_fam,
    };
  }
  return map;
}

export function calculateSalePricesFromFamilies(cost, families = [], preferredCodFam = null) {
  const rules = buildTariffRuleMap(families, preferredCodFam);
  const prices = Object.fromEntries(LEGACY_SALE_PRICE_FIELDS.map((field) => [field, 0]));
  for (const tarifa of FAMILY_TARIFF_OPTIONS) {
    const rule = rules[tarifa];
    if (!rule) continue;
    prices[LEGACY_SALE_PRICE_FIELDS[tarifa - 1]] = calculateTariffPrice(cost, rule);
  }
  return prices;
}

export function getOtherFamiliesForTariff(families = [], tarifa, excludeCodFam = null) {
  const tariff = toFamilyTariff(tarifa);
  const exclude = normalizeFamilyCode(excludeCodFam);
  if (!tariff) return [];
  return families.filter((family) => {
    if (toFamilyTariff(family?.tarifa) !== tariff) return false;
    if (exclude && normalizeFamilyCode(family.cod_fam) === exclude) return false;
    return true;
  });
}

export function getConflictingTariffFamilies(families, payload = {}) {
  return getOtherFamiliesForTariff(families, payload.tarifa, payload.excludeCodFam).filter(
    (family) => !familyTariffParamsEqual(family, payload)
  );
}

export function groupFamiliesByTariffParams(families = []) {
  const groups = [];
  for (const family of families) {
    const existing = groups.find((group) => familyTariffParamsEqual(group, family));
    if (existing) existing.families.push(family);
    else groups.push({ ...familyTariffParams(family), families: [family] });
  }
  return groups;
}

export function salePriceMismatches(currentPrices = {}, expectedPrices = {}) {
  return LEGACY_SALE_PRICE_FIELDS.map((field, index) => {
    const current = toFamilyNumber(currentPrices[field]);
    const expected = toFamilyNumber(expectedPrices[field]);
    return {
      field,
      tarifa: index + 1,
      current,
      expected,
      mismatched: Math.abs(current - expected) > SALE_PRICE_MISMATCH_TOLERANCE,
    };
  }).filter((item) => item.mismatched);
}

function sortByOrder(left, right) {
  return (Number(left.sort_order || 0) - Number(right.sort_order || 0)) || String(left.id || "").localeCompare(String(right.id || ""));
}

function normalizeTemplate(template) {
  const lines = [...(template.lines || [])].sort(sortByOrder);
  const processLines = lines
    .filter((line) => line.line_type === "process")
    .map((line) => ({
      ...line,
      child_lines: lines.filter((child) => child.parent_template_line_id === line.id).sort(sortByOrder),
    }));
  const orphanMaterialLines = lines.filter((line) => line.line_type === "material" && !line.parent_template_line_id);

  return { ...template, lines, process_lines: processLines, orphan_material_lines: orphanMaterialLines };
}

function normalizeCostSheet(sheet) {
  const materialLines = [...(sheet.material_lines || [])].sort(sortByOrder);
  const processLines = [...(sheet.process_lines || [])].sort(sortByOrder);
  const processIds = new Set(processLines.map((line) => line.id));
  const normalizedProcessLines = processLines.map((line) => {
    const childMaterials = materialLines.filter((material) => material.process_line_id === line.id).sort(sortByOrder);
    const documents = [...(line.documents || [])].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
    const normalizedLine = { ...line, material_lines: childMaterials, documents };
    return {
      ...normalizedLine,
      material_total: childMaterials.reduce(
        (sum, material) => sum + Number(material.line_cost ?? calculateMaterialLineCost(material)),
        0
      ),
      total_cost: calculateProcessTotal(normalizedLine),
    };
  });
  const orphanMaterialLines = materialLines.filter((line) => !line.process_line_id || !processIds.has(line.process_line_id));
  const totals = calculateSheetTotals({ ...sheet, material_lines: materialLines, process_lines: processLines });

  return {
    ...sheet,
    material_lines: materialLines,
    process_lines: normalizedProcessLines,
    orphan_material_lines: orphanMaterialLines,
    totals,
  };
}

export async function checkManufacturingSchema() {
  const checks = await Promise.all([
    supabase.from("mfg_products").select("id", { count: "exact", head: true }),
    supabase.from("familia").select("cod_fam", { count: "exact", head: true }),
    supabase.from("mfc_temporada").select("cod_tem", { count: "exact", head: true }),
    supabase.from("mfg_suppliers").select("id", { count: "exact", head: true }),
    supabase.from("mfg_materials").select("id", { count: "exact", head: true }),
    supabase.from("mfg_product_cost_sheets").select("id", { count: "exact", head: true }),
    supabase.from("mfg_cost_sheet_process_documents").select("id", { count: "exact", head: true }),
    supabase.from("mfg_simulation_runs").select("id", { count: "exact", head: true }),
    supabase.from("mfg_simulation_items").select("id", { count: "exact", head: true }),
    supabase.from("mfg_simulation_color_lines").select("id", { count: "exact", head: true }),
    supabase.from("mfg_simulation_cost_lines").select("id", { count: "exact", head: true }),
    supabase.from("mfg_production_orders").select("id", { count: "exact", head: true }),
    supabase.from("mfg_production_order_products").select("id", { count: "exact", head: true }),
    supabase.from("mfg_production_order_color_lines").select("id", { count: "exact", head: true }),
    supabase.from("mfg_production_order_cost_lines").select("id", { count: "exact", head: true }),
  ]);
  const error = checks.find((result) => result.error)?.error || null;
  return { ok: !error, error };
}

export async function fetchManufacturingProducts({
  searchTerm = "",
  status = "active",
  temporada = "",
  codFam = "",
  sheetStage = "",
  starFilter = "",
  garmentType = "",
  includePendingChecks = false,
  offset = 0,
  limit = 80,
} = {}) {
  const normalizedSearch = searchTerm.trim();
  let query = (sheetStage ? supabase.rpc("mfg_products_at_stage", { p_stage: sheetStage }) : supabase.from("mfg_products"))
    .select(`${MFG_PRODUCT_SELECT}, cost_sheet:mfg_product_cost_sheets!mfg_product_cost_sheets_product_id_fkey(id,validation_status,design_status,pattern_status,production_status,workflow_checks)`)
    .order("code", { ascending: true });

  if (status && status !== "all") query = query.eq("status", status);
  if (temporada) query = query.eq("temporada", temporada);
  if (codFam) query = query.eq("cod_fam", codFam);
  if (garmentType === "none") query = query.is("garment_type_id", null);
  else if (garmentType) query = query.eq("garment_type_id", garmentType);
  if (starFilter === "yes" || starFilter === "no") query = query.eq("producto_estrella", starFilter === "yes");
  if (normalizedSearch) {
    const escapedSearch = normalizedSearch.replace(/[,%()]/g, " ");
    query = query.or(`code.ilike.%${escapedSearch}%,description.ilike.%${escapedSearch}%,temporada.ilike.%${escapedSearch}%,cod_fam.ilike.%${escapedSearch}%`);
  }

  if (limit) query = query.range(offset, offset + limit - 1);

  const result = await runQuery(query);
  if (result.error || !includePendingChecks || !WORKFLOW_LABELS[sheetStage]) return result;
  const ids = result.data.map(product => productCostSheet(product)?.id).filter(Boolean);
  if (!ids.length) return result;
  const issues = await runQuery(supabase.rpc("mfg_pending_workflow_issues", { p_sheets: ids, p_stage: sheetStage }));
  if (issues.error) return { ...result, data: null, error: issues.error };
  return { ...result, data: result.data.map(product => ({ ...product,
    pending_workflow_issues: issues.data.filter(issue => issue.sheet_id === productCostSheet(product)?.id),
  })) };
}

export async function manufacturingProductCodeExists(code, excludeId = null) {
  const normalized = String(code || "").trim();
  if (!normalized) return { data: false, error: null };
  let query = supabase.from("mfg_products").select("id").eq("code", normalized);
  if (excludeId) query = query.neq("id", excludeId);
  const result = await runQuery(query.maybeSingle(), null);
  if (result.error) return result;
  return { data: Boolean(result.data?.id), error: null };
}

export async function countManufacturingProducts({ codFam = "", temporada = "", status = "" } = {}) {
  let query = supabase.from("mfg_products").select("id", { count: "exact", head: true });
  if (codFam) query = query.eq("cod_fam", codFam);
  if (temporada) query = query.eq("temporada", temporada);
  if (status && status !== "all") query = query.eq("status", status);
  try {
    const { count, error } = await query;
    if (error) throw error;
    return { data: count || 0, error: null };
  } catch (error) {
    return { data: 0, error };
  }
}

export async function bulkUpdateManufacturingProductStatus({ codFam = "", temporada = "", status } = {}) {
  if (!["active", "obsolete"].includes(status)) {
    return { data: null, error: new Error("Estado no válido") };
  }
  if (!codFam && !temporada) {
    return { data: null, error: new Error("Selecciona familia y/o temporada") };
  }
  let query = supabase.from("mfg_products").update({ status }, { count: "exact" });
  if (codFam) query = query.eq("cod_fam", codFam);
  if (temporada) query = query.eq("temporada", temporada);
  const { error, count } = await query;
  return { data: count || 0, error };
}

export async function fetchManufacturingProduct(productId) {
  if (!productId) return { data: null, error: null };
  return runQuery(
    supabase
      .from("mfg_products")
      .select(MFG_PRODUCT_SELECT)
      .eq("id", productId)
      .maybeSingle(),
    null
  );
}

export async function fetchSalesProductsForManufacturing(searchTerm = "") {
  let query = supabase
    .from("products")
    .select("id, code, description, price, temporada, composicion, sizes, colors, active")
    .order("code", { ascending: true })
    .limit(80);

  if (searchTerm) query = query.or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,temporada.ilike.%${searchTerm}%`);

  return runQuery(query);
}

export async function saveManufacturingProduct(payload, id = null, options = {}) {
  const { seedFamilyTemplate = !id } = options;
  if ((payload.codigo_patron?.trim() || "").length > 20) return { data: null, error: new Error("El código de patrón no puede superar 20 caracteres") };
  const images = Array.isArray(payload.images) ? payload.images : [];
  const maxMainImageIndex = Math.max(images.length - 1, 0);
  const mainImageIndex = Math.min(Math.max(Number(payload.main_image_index || 0), 0), maxMainImageIndex);
  const data = {
    code: payload.code?.trim(),
    description: payload.description?.trim(),
    temporada: payload.temporada?.trim().toUpperCase() || null,
    composicion: payload.composicion?.trim() || null,
    codigo_patron: payload.codigo_patron?.trim() || null,
    ubicacion_patron: payload.ubicacion_patron?.trim() || null,
    cod_fam: payload.cod_fam?.trim() || null,
    status: payload.status || "active",
    images,
    main_image_index: mainImageIndex,
  };
  if ("garment_type_id" in payload) data.garment_type_id = payload.garment_type_id || null;
  if ("producto_estrella" in payload) data.producto_estrella = Boolean(payload.producto_estrella);
  if ("observaciones" in payload) data.observaciones = payload.observaciones?.trim() || null;
  if ("sales_product_id" in payload) data.sales_product_id = payload.sales_product_id || null;
  if ("sizes" in payload) data.sizes = Array.isArray(payload.sizes) ? payload.sizes : [];
  if ("colors" in payload) data.colors = Array.isArray(payload.colors) ? payload.colors : [];
  if ("sync_to_sales" in payload) data.sync_to_sales = Boolean(payload.sync_to_sales);
  for (const field of LEGACY_SALE_PRICE_FIELDS) {
    if (field in payload) data[field] = Number(payload[field] || 0);
  }
  if (id) {
    const locked = await rejectIfProductSheetValidated(id);
    if (locked) return locked;
  }
  if (!id && seedFamilyTemplate) {
    if (!data.temporada) return { data: null, error: new Error("La temporada es obligatoria") };
    data.code = productCodeWithSeason(data.code, data.temporada);
    const result = await runQuery(supabase.rpc("mfg_create_product_with_template", {
      p_product: data, p_request_id: options.requestId || crypto.randomUUID(),
    }), null);
    if (result.error) return result;
    return { data: result.data.product, error: null, familyTemplate: result.data.familyTemplate };
  }
  return runQuery(id
    ? supabase.from("mfg_products").update(data).eq("id", id).select().single()
    : supabase.from("mfg_products").insert(data).select().single(), null);
}

export async function updateManufacturingProductObservations(id, observations) {
  const locked = await rejectIfProductSheetValidated(id);
  if (locked) return locked;
  return runQuery(supabase.from("mfg_products")
    .update({ observaciones: observations.trim() || null }).eq("id", id).select("id, observaciones").single(), null);
}

export async function deleteManufacturingProduct(id) {
  if (!id) return { data: null, error: new Error("Producto no valido") };
  const locked = await rejectIfProductSheetValidated(id);
  if (locked) return locked;
  return runQuery(supabase.rpc("delete_mfg_product", { p_product_id: id }), null);
}

export async function createManufacturingProductFromSalesProduct(product) {
  if (!product?.id) return { data: null, error: new Error("Producto comercial no valido") };
  const existing = await runQuery(
    supabase.from("mfg_products").select("*").eq("sales_product_id", product.id).maybeSingle(),
    null
  );
  if (existing.error) return existing;
  if (existing.data) return existing;

  return saveManufacturingProduct({
    sales_product_id: product.id,
    code: product.code,
    description: product.description,
    temporada: product.temporada,
    composicion: product.composicion,
    sizes: product.sizes || [],
    colors: product.colors || [],
    status: "active",
    sync_to_sales: true,
    precio_v: product.price || 0,
  }, null, { seedFamilyTemplate: false });
}

export async function previewDuplicateManufacturingProduct(sourceProduct, { codFam } = {}) {
  const sourceId = sourceProduct?.id;
  if (!sourceId) return { data: null, error: new Error("Producto origen no válido") };

  const sourceResult = await fetchManufacturingProduct(sourceId);
  if (sourceResult.error) return sourceResult;
  const source = sourceResult.data;
  if (!source) return { data: null, error: new Error("No se encontró el producto origen") };

  const sourceFam = source.cod_fam?.trim() || "";
  const destFam = String(codFam ?? source.cod_fam ?? "").trim();
  const context = await loadDuplicateTemplateContext(sourceId, sourceFam, destFam);
  if (context.error) return context;

  return {
    data: {
      ...analyzeDuplicateCostSheet({
        sourceCodFam: sourceFam,
        destCodFam: destFam,
        sourceTemplate: context.sourceTemplate,
        destTemplate: context.destTemplate,
        sourceSheet: context.sourceSheet,
      }),
      sourceFamily: sourceFam,
      destFamily: destFam,
    },
    error: null,
  };
}

export async function duplicateManufacturingProduct(sourceProduct, { code, codFam, temporada, specialSizes = false, keepReference = false, requestId } = {}) {
  if (!temporada?.trim()) return { data: null, error: new Error("La temporada es obligatoria") };
  if (keepReference && (specialSizes || !canPreserveDuplicateReference(sourceProduct?.cod_fam, codFam))) {
    return { data: null, error: new Error("Para conservar la referencia mantén la familia de origen y desmarca tallas especiales") };
  }
  const result = await runQuery(supabase.rpc("mfg_duplicate_product", {
    p_keep_reference: keepReference,
    p_season: temporada.trim().toUpperCase(),
    p_source: sourceProduct?.id, p_code: keepReference ? code?.trim() : productCodeWithSeason(code, temporada), p_family: codFam || null,
    p_special: specialSizes, p_request_id: requestId || crypto.randomUUID(),
  }), null);
  if (result.error) return result;
  return fetchManufacturingProduct(result.data);
}

async function loadDuplicateTemplateContext(sourceProductId, sourceFam, destFam) {
  const [sourceTemplateResult, destTemplateResult, sheetsResult] = await Promise.all([
    fetchTemplateByFamily(sourceFam),
    fetchTemplateByFamily(destFam),
    fetchCostSheets(sourceProductId),
  ]);
  if (sourceTemplateResult.error) return sourceTemplateResult;
  if (destTemplateResult.error) return destTemplateResult;
  if (sheetsResult.error) return sheetsResult;
  return {
    sourceTemplate: sourceTemplateResult.data,
    destTemplate: destTemplateResult.data,
    sourceSheet: sheetsResult.data?.[0] || null,
    error: null,
  };
}

export async function publishManufacturingProductToSales(productId) {
  try {
    const { data, error } = await supabase.rpc("publish_mfg_product_to_sales", { p_mfg_product_id: productId });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Manufacturing product publish error:", error);
    return { data: null, error };
  }
}

export async function fetchSupplierTypes() {
  return runQuery(supabase.from("mfg_supplier_types").select("*").order("erp_code"));
}

export async function fetchSuppliers(searchTerm = "", { activeOnly = false } = {}) {
  let query = supabase
    .from("mfg_suppliers")
    .select("*, supplier_type:mfg_supplier_types(id, erp_code, description)")
    .order("name", { ascending: true });

  if (activeOnly) query = query.eq("is_active", true);
  if (searchTerm) {
    query = query.or(`erp_code.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
  }

  return runQuery(query);
}

export async function fetchImportBatches() {
  return runQuery(
    supabase.from("mfg_import_batches").select("*").order("started_at", { ascending: false }).limit(30)
  );
}

export async function fetchFamilies(searchTerm = "") {
  let query = supabase.from("familia").select("*").order("cod_fam", { ascending: true });
  if (searchTerm) query = query.or(`cod_fam.ilike.%${searchTerm}%,descrip.ilike.%${searchTerm}%`);
  return runQuery(query);
}

export async function saveFamily(payload, codFam = null) {
  const color = payload.magnet_label_color?.trim() || null;
  if (color && !/^#[0-9a-f]{6}$/i.test(color)) return { data: null, error: new Error("El color de etiquetas imán debe tener formato #RRGGBB") };
  const data = {
    ...(payload.magnet_label_color !== undefined ? { magnet_label_color: color } : {}),
    cod_fam: payload.cod_fam?.trim().toUpperCase(),
    descrip: payload.descrip?.trim(),
    iniciales: payload.iniciales?.trim().toUpperCase() || null,
    tarifa: toFamilyTariff(payload.tarifa),
    valor_fijo: toFamilyNumber(payload.valor_fijo),
    incremento: toFamilyNumber(payload.incremento),
    condicion: toFamilyNumber(payload.condicion),
  };
  const query = codFam
    ? supabase.from("familia").update(data).eq("cod_fam", codFam).select().single()
    : supabase.from("familia").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteFamily(codFam) {
  return runQuery(supabase.from("familia").delete().eq("cod_fam", codFam), null);
}

export async function syncFamiliesTariffParams(tarifa, params) {
  const tariff = toFamilyTariff(tarifa);
  if (!tariff) return { data: null, error: new Error("Tarifa no válida") };
  return runQuery(
    supabase
      .from("familia")
      .update({
        valor_fijo: toFamilyNumber(params?.valor_fijo),
        incremento: toFamilyNumber(params?.incremento),
        condicion: toFamilyNumber(params?.condicion),
      })
      .eq("tarifa", tariff)
      .select(),
    []
  );
}

export async function getNextFamilyProductCode(codFam, temporada) {
  const normalized = codFam?.trim();
  const season = temporada?.trim().toUpperCase();
  if (!season) return { data: null, error: new Error("Selecciona una temporada para generar el código") };
  if (!normalized) return { data: null, error: new Error("Selecciona una familia para generar el código") };
  try {
    const { data, error } = await supabase.rpc("mfg_next_family_product_code", { p_cod_fam: normalized, p_season: season });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Manufacturing next family code error:", error);
    return { data: null, error };
  }
}

export async function fetchSeasons(searchTerm = "") {
  let query = supabase.from("mfc_temporada").select("*").order("orden", { ascending: false }).order("cod_tem", { ascending: false });
  if (searchTerm) query = query.or(`cod_tem.ilike.%${searchTerm}%,descrip.ilike.%${searchTerm}%`);
  return runQuery(query);
}

export async function saveSeason(payload, codTem = null) {
  const data = {
    cod_tem: payload.cod_tem?.trim().toUpperCase(),
    descrip: payload.descrip?.trim(),
    codigo: payload.codigo === "" || payload.codigo === null || payload.codigo === undefined ? null : Number(payload.codigo),
    defecto: Boolean(payload.defecto),
    fecha_i: payload.fecha_i || null,
    fecha_f: payload.fecha_f || null,
  };
  if (payload.orden !== "" && payload.orden != null) data.orden = Number(payload.orden);
  const query = codTem
    ? supabase.from("mfc_temporada").update(data).eq("cod_tem", codTem).select().single()
    : supabase.from("mfc_temporada").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteSeason(codTem) {
  return runQuery(supabase.from("mfc_temporada").delete().eq("cod_tem", codTem), null);
}

export async function fetchProcesses({ activeOnly = false, searchTerm = "" } = {}) {
  let query = supabase.from("mfg_manufacturing_processes").select("*");
  if (activeOnly) query = query.eq("is_active", true);
  if (searchTerm) query = query.or(`description.ilike.%${searchTerm}%,notes.ilike.%${searchTerm}%`);
  return runQuery(query.order("description", { ascending: true }));
}

export async function saveProcess(payload, id = null) {
  const planningOrder = payload.planning_order === "" || payload.planning_order == null ? null : Number(payload.planning_order);
  if (planningOrder !== null && (!Number.isInteger(planningOrder) || planningOrder < 1 || planningOrder > 2147483647)) {
    return { data: null, error: { message: "El orden en el cuadrante debe ser un entero positivo." } };
  }
  const data = {
    planning_order: planningOrder,
    allows_color_variants: Boolean(payload.allows_color_variants),
    document_requirements: payload.document_requirements || [],
    requires_documents: Boolean(payload.requires_documents),
    document_stage: payload.document_stage || "pattern",
    required_document_count: Number(payload.required_document_count ?? 1),
    price_stage: payload.price_stage || null,
    consumption_stage: payload.consumption_stage || null,
    description: payload.description?.trim(),
    notes: payload.notes || null,
    excludes_manufacturing: Boolean(payload.excludes_manufacturing),
    is_overhead: Boolean(payload.is_overhead),
    albaran_material_process_id: payload.albaran_material_process_id || null,
    albaran_cutting_layout: Boolean(payload.albaran_cutting_layout),
    is_active: payload.is_active !== false,
  };
  if (payload.legacy_code !== "" && payload.legacy_code !== null && payload.legacy_code !== undefined) {
    data.legacy_code = Number(payload.legacy_code);
  }

  const query = id
    ? supabase.from("mfg_manufacturing_processes").update(data).eq("id", id).select().single()
    : supabase.from("mfg_manufacturing_processes").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteProcess(id) {
  return runQuery(supabase.from("mfg_manufacturing_processes").delete().eq("id", id), null);
}

export async function fetchMaterials({ activeOnly = false, searchTerm = "" } = {}) {
  let query = supabase.from("mfg_materials").select("*");
  if (activeOnly) query = query.eq("is_active", true);
  if (searchTerm) {
    const numericTerm = Number(searchTerm);
    query = Number.isFinite(numericTerm)
      ? query.or(`code.eq.${numericTerm},internal_description.ilike.%${searchTerm}%,season_code.ilike.%${searchTerm}%`)
      : query.or(`internal_description.ilike.%${searchTerm}%,season_code.ilike.%${searchTerm}%`);
  }
  return runQuery(query.order("internal_description", { ascending: true }));
}

async function nextMaterialCode() {
  const result = await runQuery(
    supabase.from("mfg_materials").select("code").order("code", { ascending: false }).limit(1).maybeSingle(),
    null
  );
  return Number(result.data?.code || 0) + 1;
}

function isMaterialCodeConflict(error) {
  const message = error?.message || "";
  return error?.code === "23505" || /mfg_materials_code_key/i.test(message);
}

export async function saveMaterial(payload, id = null) {
  const data = {
    internal_description: payload.internal_description?.trim(),
    season_code: payload.season_code || null,
    default_unit: payload.default_unit || "ud",
    is_active: payload.is_active !== false,
  };
  if ("hide_from_planning" in payload) data.hide_from_planning = payload.hide_from_planning === true;
  const explicitCode = Number(payload.code);
  if (payload.code !== "" && payload.code !== null && payload.code !== undefined && Number.isFinite(explicitCode) && explicitCode > 0) {
    data.code = explicitCode;
  }

  if (id) {
    return runQuery(supabase.from("mfg_materials").update(data).eq("id", id).select().single(), null);
  }

  if (data.code) {
    return runQuery(supabase.from("mfg_materials").insert(data).select().single(), null);
  }

  let lastResult = { data: null, error: new Error("No se pudo generar el código de material") };
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const insertData = { ...data, code: await nextMaterialCode() };
    lastResult = await runQuery(supabase.from("mfg_materials").insert(insertData).select().single(), null);
    if (!lastResult.error || !isMaterialCodeConflict(lastResult.error)) return lastResult;
  }
  return lastResult;
}

export async function deleteMaterial(id) {
  return runQuery(supabase.from("mfg_materials").delete().eq("id", id), null);
}

// Read every page so legacy references beyond the API row limit remain searchable.
export async function fetchMaterialReferenceCatalog() {
  const rows = [];
  const pageSize = 1000;
  for (let offset = 0; ; offset += pageSize) {
    const result = await runQuery(
      supabase.from("mfg_material_suppliers")
        .select("*, supplier:mfg_suppliers(id, erp_code, name), material:mfg_materials!inner(*)")
        .order("id")
        .range(offset, offset + pageSize - 1)
    );
    if (result.error) return result;
    rows.push(...(result.data || []));
    if ((result.data || []).length < pageSize) return { data: rows, error: null };
  }
}

export async function fetchMaterialSuppliers(materialId) {
  if (!materialId) return emptyResult;
  return runQuery(
    supabase
      .from("mfg_material_suppliers")
      .select("*, supplier:mfg_suppliers(id, erp_code, name)")
      .eq("material_id", materialId)
      .order("is_primary", { ascending: false })
      .order("supplier_ref", { ascending: true })
  );
}

export async function saveMaterialSupplier(payload, id = null) {
  const data = {
    ...("is_active" in payload ? { is_active: payload.is_active !== false } : {}),
    material_id: payload.material_id,
    supplier_id: payload.supplier_id,
    supplier_ref: payload.supplier_ref || "",
    supplier_description: payload.supplier_description || null,
    purchase_price: Number(payload.purchase_price || 0),
    currency: payload.currency || "EUR",
    is_primary: Boolean(payload.is_primary),
    valid_from: payload.valid_from || null,
    valid_until: payload.valid_until || null,
  };
  const query = id
    ? supabase.from("mfg_material_suppliers").update(data).eq("id", id).select().single()
    : supabase.from("mfg_material_suppliers").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteMaterialSupplier(id) {
  return runQuery(supabase.from("mfg_material_suppliers").delete().eq("id", id), null);
}

export async function setMaterialReferenceActive(id, isActive) {
  return runQuery(supabase.from("mfg_material_suppliers")
    .update({ is_active: Boolean(isActive) }).eq("id", id).select().single(), null);
}

export async function fetchMaterialUsages() {
  const rows = [];
  for (let offset = 0; ; offset += 1000) {
    const result = await runQuery(supabase.from("mfg_material_usages").select("*").order("name").order("id").range(offset, offset + 999));
    if (result.error) return result;
    rows.push(...result.data);
    if (result.data.length < 1000) return { data: rows, error: null };
  }
}

export async function resolveMaterialUsage(name) {
  return runQuery(supabase.rpc("mfg_resolve_material_usage", { p_name: name.trim() }), null);
}

export async function setMaterialUsageActive(id, active) {
  return runQuery(supabase.from("mfg_material_usages").update({ is_active: Boolean(active) }).eq("id", id).select().single(), null);
}

export async function fetchTemplates() {
  const result = await runQuery(
    supabase.from("mfg_cost_sheet_templates").select(TEMPLATE_SELECT).order("name", { ascending: true })
  );
  return { ...result, data: (result.data || []).map(normalizeTemplate) };
}

export async function fetchTemplateByFamily(codFam) {
  const code = String(codFam || "").trim();
  if (!code) return { data: null, error: null };
  const result = await runQuery(
    supabase.from("mfg_cost_sheet_templates").select(TEMPLATE_SELECT).eq("cod_fam", code).maybeSingle(),
    null
  );
  if (result.error) return result;
  return { data: result.data ? normalizeTemplate(result.data) : null, error: null };
}

export async function saveTemplate(payload, id = null) {
  const data = {
    name: payload.name?.trim(),
    description: payload.description || null,
    template_kind: payload.template_kind || "mixed",
    is_active: payload.is_active !== false,
    cod_fam: payload.cod_fam?.trim() ? payload.cod_fam.trim().toUpperCase() : null,
  };
  const query = id
    ? supabase.from("mfg_cost_sheet_templates").update(data).eq("id", id).select().single()
    : supabase.from("mfg_cost_sheet_templates").insert(data).select().single();
  const result = await runQuery(query, null);
  if (result.error && /duplicate|unique|mfg_cost_sheet_templates_cod_fam/i.test(result.error.message || "")) {
    return { data: null, error: new Error("Ya existe una plantilla para esa familia") };
  }
  return result;
}

export async function deleteTemplate(id) {
  return runQuery(supabase.from("mfg_cost_sheet_templates").delete().eq("id", id), null);
}

export async function saveTemplateLine(payload, id = null) {
  const lineType = payload.line_type || "material";
  const data = {
    template_id: payload.template_id,
    line_type: lineType,
    material_id: lineType === "material" ? payload.material_id : null,
    process_id: lineType === "process" ? payload.process_id : null,
    parent_template_line_id: lineType === "material" ? payload.parent_template_line_id || null : null,
    material_supplier_id: lineType === "material" ? payload.material_supplier_id || null : null,
    sort_order: Number(payload.sort_order || 0),
    default_quantity: payload.default_quantity === "" ? null : Number(payload.default_quantity || 0),
    default_waste_pct: Number(payload.default_waste_pct || 0),
    default_unit_cost: payload.default_unit_cost === "" ? null : Number(payload.default_unit_cost || 0),
    notes: payload.notes || null,
    fijo_al_duplicar: Boolean(payload.fijo_al_duplicar),
    mantener_precio_origen: lineType === "process" ? Boolean(payload.mantener_precio_origen) : false,
  };
  if ("excluir_al_duplicar" in payload) data.excluir_al_duplicar = lineType === "material" && Boolean(payload.excluir_al_duplicar);
  if ("solo_si_existe_destino" in payload) data.solo_si_existe_destino = lineType === "material" && Boolean(payload.solo_si_existe_destino);
  if ("color_usage" in payload) data.color_usage = payload.color_usage || null;
  if ("requires_color" in payload) data.requires_color = payload.requires_color === true;
  const query = id
    ? supabase.from("mfg_template_lines").update(data).eq("id", id).select().single()
    : supabase.from("mfg_template_lines").insert(data).select().single();
  return runQuery(query, null);
}

export async function setTemplateMaterialDestinationOnly(id, checked) {
  return runQuery(supabase.from("mfg_template_lines").update({ solo_si_existe_destino: Boolean(checked) })
    .eq("id", id).eq("line_type", "material").select().single(), null);
}

export async function setTemplateMaterialExcluded(id, checked) {
  return runQuery(supabase.from("mfg_template_lines").update({ excluir_al_duplicar: Boolean(checked) })
    .eq("id", id).eq("line_type", "material").select().single(), null);
}

export async function deleteTemplateLine(id) {
  return runQuery(supabase.from("mfg_template_lines").delete().eq("id", id), null);
}

export async function applyTemplateToCostSheet(costSheetId, templateId) {
  if (!costSheetId || !templateId) return { data: null, error: new Error("Selecciona escandallo y plantilla") };
  const locked = await rejectIfSheetValidated(costSheetId);
  if (locked) return locked;
  return runQuery(
    supabase.rpc("mfg_replace_cost_sheet_from_template", {
      p_cost_sheet_id: costSheetId,
      p_template_id: templateId,
    }),
    null
  );
}

function familyTemplateResult(product, extras = {}) {
  return {
    data: product ? { product } : null,
    error: extras.error || null,
    warning: extras.warning || null,
    familyTemplate: extras.familyTemplate || { applied: false, reason: extras.reason || "error" },
  };
}

export function describeFamilyTemplateSeed(result) {
  if (result?.warning) return result.warning;
  if (result?.familyTemplate?.applied) {
    return `Escandallo generado desde la plantilla de la familia (${Number(result.familyTemplate.process_count || 0)} procesos, ${Number(result.familyTemplate.material_count || 0)} materiales).`;
  }
  if (result?.familyTemplate?.reason === "no_template") {
    return "La familia no tiene plantilla asociada. El escandallo queda vacío.";
  }
  return "";
}

export async function seedCostSheetFromFamilyTemplate(productId) {
  if (!productId) return familyTemplateResult(null, { error: new Error("Artículo no válido") });

  const productResult = await fetchManufacturingProduct(productId);
  if (productResult.error) return familyTemplateResult(productResult.data, { error: productResult.error });
  const product = productResult.data;
  if (!product) return familyTemplateResult(null, { error: new Error("Artículo no encontrado") });

  const familyCode = product.cod_fam?.trim();
  if (!familyCode) return familyTemplateResult(product, { reason: "no_family" });

  const templateResult = await fetchTemplateByFamily(familyCode);
  if (templateResult.error) return familyTemplateResult(product, { error: templateResult.error });
  if (!templateResult.data?.id) return familyTemplateResult(product, { reason: "no_template" });

  const sheetResult = await fetchOrCreateCostSheet(productId);
  if (sheetResult.error) return familyTemplateResult(product, { error: sheetResult.error });
  const sheet = sheetResult.data;
  if (!sheet?.id) return familyTemplateResult(product, { error: new Error("No se pudo crear el escandallo") });

  const hasLines = (sheet.material_lines?.length || 0) + (sheet.process_lines?.length || 0) > 0;
  if (hasLines) return familyTemplateResult(product, { reason: "already_has_lines" });

  const applied = await applyTemplateToCostSheet(sheet.id, templateResult.data.id);
  if (applied.error) return familyTemplateResult(product, { error: applied.error });

  const recalc = await recalculateProductSalePrices(productId);
  if (recalc.error) {
    return familyTemplateResult(product, {
      warning: recalc.error.message || "El escandallo se generó, pero no se pudieron recalcular las tarifas.",
      familyTemplate: { applied: true, ...(applied.data || {}) },
    });
  }

  return {
    data: { product: recalc.data || product, sheet },
    error: null,
    warning: null,
    familyTemplate: { applied: true, ...(applied.data || {}) },
  };
}

const COST_SHEET_SELECT =
  "*, color_preset:mfg_cost_sheet_color_presets(variants, revision), material_lines:mfg_cost_sheet_material_lines(*, material:mfg_materials(*), material_supplier:mfg_material_suppliers(*, supplier:mfg_suppliers(id, erp_code, name))), process_lines:mfg_cost_sheet_process_lines(*, process:mfg_manufacturing_processes(*), supplier:mfg_suppliers(id, erp_code, name), documents:mfg_cost_sheet_process_documents(*))";

export async function fetchCostSheets(productId) {
  if (!productId) return emptyResult;
  const result = await runQuery(
    supabase
      .from("mfg_product_cost_sheets")
      .select(COST_SHEET_SELECT)
      .eq("product_id", productId)
      .order("created_at", { ascending: true })
  );
  return { ...result, data: (result.data || []).map(normalizeCostSheet) };
}

export async function fetchCostSheetsByIds(ids = []) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (!uniqueIds.length) return { data: new Map(), error: null };
  const result = await runQuery(
    supabase.from("mfg_product_cost_sheets").select(COST_SHEET_SELECT).in("id", uniqueIds)
  );
  if (result.error) return { data: new Map(), error: result.error };
  return {
    data: new Map((result.data || []).map((sheet) => [sheet.id, normalizeCostSheet(sheet)])),
    error: null,
  };
}

const COST_SHEET_STAGE_COLUMNS = ["design_status", "pattern_status", "production_status", "validation_status"];

export const COST_SHEET_VALIDATED_ERROR = new Error(
  "El escandallo está validado. Quita la validación para poder modificar la ficha."
);

async function rejectIfSheetValidated(costSheetId) {
  if (!costSheetId) return null;
  const { data, error } = await supabase
    .from("mfg_product_cost_sheets")
    .select("validation_status")
    .eq("id", costSheetId)
    .maybeSingle();
  if (error) return { data: null, error };
  if (data?.validation_status === "validated") {
    return { data: null, error: COST_SHEET_VALIDATED_ERROR };
  }
  return null;
}

async function rejectIfProductSheetValidated(productId) {
  if (!productId) return null;
  const { data, error } = await supabase
    .from("mfg_product_cost_sheets")
    .select("validation_status")
    .eq("product_id", productId)
    .maybeSingle();
  if (error) return { data: null, error };
  if (data?.validation_status === "validated") {
    return { data: null, error: COST_SHEET_VALIDATED_ERROR };
  }
  return null;
}

async function loadProductSalePriceContext(productId) {
  if (!productId) return { data: null, error: new Error("Artículo no válido") };

  const [productResult, familiesResult, sheetsResult] = await Promise.all([
    fetchManufacturingProduct(productId),
    fetchFamilies(),
    fetchCostSheets(productId),
  ]);
  if (productResult.error) return productResult;
  if (!productResult.data) return { data: null, error: new Error("Artículo no encontrado") };
  if (familiesResult.error) return familiesResult;
  if (sheetsResult.error) return sheetsResult;

  const cost = Number(sheetsResult.data?.[0]?.totals?.total || 0);
  const prices = calculateSalePricesFromFamilies(cost, familiesResult.data || [], productResult.data.cod_fam);
  return {
    data: {
      product: productResult.data,
      families: familiesResult.data || [],
      cost,
      prices,
    },
    error: null,
  };
}

export async function recalculateProductSalePrices(productId) {
  const context = await loadProductSalePriceContext(productId);
  if (context.error) return context;

  const locked = await rejectIfProductSheetValidated(productId);
  if (locked) return locked;

  return runQuery(
    supabase.from("mfg_products").update(context.data.prices).eq("id", productId).select(MFG_PRODUCT_SELECT).single(),
    null
  );
}

export async function updateProductSaleTariff(productId, tariffNumber) {
  const tarifa = toFamilyTariff(tariffNumber);
  if (!tarifa) return { data: null, error: new Error("Tarifa no válida") };

  const context = await loadProductSalePriceContext(productId);
  if (context.error) return context;

  const locked = await rejectIfProductSheetValidated(productId);
  if (locked) return locked;

  const field = LEGACY_SALE_PRICE_FIELDS[tarifa - 1];
  return runQuery(
    supabase
      .from("mfg_products")
      .update({ [field]: context.data.prices[field] })
      .eq("id", productId)
      .select(MFG_PRODUCT_SELECT)
      .single(),
    null
  );
}

export async function fetchOrCreateCostSheet(productId) {
  if (!productId) return { data: null, error: null };
  const existing = await fetchCostSheets(productId);
  if (existing.error) return { data: null, error: existing.error };
  if (existing.data.length > 0) return { data: existing.data[0], error: null };
  const created = await saveCostSheet({ product_id: productId });
  if (created.error) return { data: null, error: created.error };
  const refetched = await fetchCostSheets(productId);
  return {
    data: refetched.data?.[0] || normalizeCostSheet(created.data || { product_id: productId }),
    error: refetched.error,
  };
}

export async function saveCostSheet(payload, id = null) {
  if (id) {
    const { data: current, error } = await supabase
      .from("mfg_product_cost_sheets")
      .select("validation_status")
      .eq("id", id)
      .maybeSingle();
    if (error) return { data: null, error };
    const currentlyValidated = current?.validation_status === "validated";
    const nextValidation = "validation_status" in payload ? payload.validation_status || null : current?.validation_status || null;
    if (currentlyValidated && nextValidation === "validated") {
      return { data: null, error: COST_SHEET_VALIDATED_ERROR };
    }
  }
  const data = {
    product_id: payload.product_id,
    notes: payload.notes || null,
    source_product_id: payload.source_product_id || null,
  };
  for (const column of COST_SHEET_STAGE_COLUMNS) {
    if (column in payload) data[column] = payload[column] || null;
  }
  const query = id
    ? supabase.from("mfg_product_cost_sheets").update(data).eq("id", id).select().single()
    : supabase.from("mfg_product_cost_sheets").insert(data).select().single();
  return runQuery(query, null);
}

export async function saveCostSheetColorPresets(sheetId, variants, expectedRevision = 0, excludedLineIds = null) {
  return runQuery(supabase.rpc(excludedLineIds === null ? "mfg_save_cost_sheet_color_presets" : "mfg_save_color_preset_editor", {
    ...(excludedLineIds === null ? {} : { p_excluded_lines: excludedLineIds }),
    p_sheet: sheetId, p_variants: variants, p_expected_revision: expectedRevision,
  }), null);
}

export async function deleteCostSheet(id) {
  return runQuery(supabase.from("mfg_product_cost_sheets").delete().eq("id", id), null);
}

export async function saveMaterialLine(payload, id = null) {
  const locked = await rejectIfSheetValidated(payload.cost_sheet_id);
  if (locked) return locked;
  const data = {
    cost_sheet_id: payload.cost_sheet_id,
    process_line_id: payload.process_line_id || null,
    material_id: payload.material_id,
    material_supplier_id: payload.material_supplier_id || null,
    quantity: Number(payload.quantity || 0),
    unit: payload.unit || null,
    waste_pct: Number(payload.waste_pct || 0),
    unit_cost: Number(payload.unit_cost || 0),
    sort_order: Number(payload.sort_order || 0),
    notes: payload.notes || null,
  };
  if ("color_variant_excluded" in payload) data.color_variant_excluded = payload.color_variant_excluded === true;
  if ("color_usage" in payload) data.color_usage = payload.color_usage || null;
  if ("requires_color" in payload) data.requires_color = payload.requires_color === true;
  const query = id
    ? supabase.from("mfg_cost_sheet_material_lines").update(data).eq("id", id).select().single()
    : supabase.from("mfg_cost_sheet_material_lines").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteMaterialLine(id) {
  return runQuery(supabase.from("mfg_cost_sheet_material_lines").delete().eq("id", id), null);
}

export async function saveProcessLine(payload, id = null) {
  const locked = await rejectIfSheetValidated(payload.cost_sheet_id);
  if (locked) return locked;
  const data = {
    cost_sheet_id: payload.cost_sheet_id,
    process_id: payload.process_id,
    supplier_id: payload.supplier_id || null,
    quantity: Number(payload.quantity || 0),
    unit_cost: Number(payload.unit_cost || 0),
    sort_order: Number(payload.sort_order || 0),
    notes: payload.notes || null,
  };
  const query = id
    ? supabase.from("mfg_cost_sheet_process_lines").update(data).eq("id", id).select().single()
    : supabase.from("mfg_cost_sheet_process_lines").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteProcessLine(id) {
  return runQuery(supabase.from("mfg_cost_sheet_process_lines").delete().eq("id", id), null);
}

async function updateLineSortOrders(tableName, lines) {
  const updates = (lines || [])
    .map((line, index) => ({
      id: line.id,
      sort_order: index + 1,
      previous: Number(line.sort_order || 0),
    }))
    .filter((item) => item.id && item.sort_order !== item.previous);

  if (!updates.length) return { data: true, error: null };

  const results = await Promise.all(
    updates.map((item) =>
      runQuery(supabase.from(tableName).update({ sort_order: item.sort_order }).eq("id", item.id), null)
    )
  );
  const error = results.find((result) => result.error)?.error || null;
  return { data: !error, error };
}

export async function reorderCostSheetProcessLines(lines) {
  return updateLineSortOrders("mfg_cost_sheet_process_lines", lines);
}

export async function reorderCostSheetMaterialLines(lines) {
  return updateLineSortOrders("mfg_cost_sheet_material_lines", lines);
}

export async function reorderTemplateLines(lines) {
  return updateLineSortOrders("mfg_template_lines", lines);
}

export async function fetchProcessDocuments(processLineId) {
  if (!processLineId) return emptyResult;
  return runQuery(
    supabase
      .from("mfg_cost_sheet_process_documents")
      .select("*")
      .eq("process_line_id", processLineId)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: false })
  );
}

export async function saveProcessDocument(payload, id = null) {
  const data = {
    document_requirement_id: payload.document_requirement_id || null,
    process_line_id: payload.process_line_id,
    document_type: payload.document_type || "other",
    title: payload.title?.trim(),
    storage_path: payload.storage_path || null,
    external_url: payload.external_url || null,
    file_name: payload.file_name || null,
    mime_type: payload.mime_type || null,
    file_size_bytes: payload.file_size_bytes ? Number(payload.file_size_bytes) : null,
    notes: payload.notes || null,
    is_default: Boolean(payload.is_default),
  };
  for (const key of ['design_storage_path', 'design_version', 'expected_design_revision']) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) data[key] = payload[key];
  }
  if (payload.design_revision != null && Object.prototype.hasOwnProperty.call(data, 'design_storage_path') && !Object.prototype.hasOwnProperty.call(data, 'expected_design_revision')) {
    data.expected_design_revision = payload.design_revision;
  }
  return runQuery(supabase.rpc("mfg_save_process_document", { p_document: data, p_id: id }).single(), null);
}

export async function deleteProcessDocument(id) {
  return runQuery(supabase.from("mfg_cost_sheet_process_documents").delete().eq("id", id), null);
}

export async function fetchTechnicalDocuments(productId) {
  if (!productId) return emptyResult;
  return runQuery(
    supabase
      .from("mfg_product_documents")
      .select("*")
      .eq("product_id", productId)
      .order("created_at", { ascending: false })
  );
}

export async function saveTechnicalDocument(payload, id = null) {
  const data = {
    product_id: payload.product_id,
    document_type: payload.document_type || "other",
    title: payload.title?.trim(),
    storage_path: payload.storage_path || null,
    external_url: payload.external_url || null,
    file_name: payload.file_name || null,
    mime_type: payload.mime_type || null,
    file_size_bytes: payload.file_size_bytes ? Number(payload.file_size_bytes) : null,
    notes: payload.notes || null,
  };
  const query = id
    ? supabase.from("mfg_product_documents").update(data).eq("id", id).select().single()
    : supabase.from("mfg_product_documents").insert(data).select().single();
  return runQuery(query, null);
}

export async function deleteTechnicalDocument(id) {
  return runQuery(supabase.from("mfg_product_documents").delete().eq("id", id), null);
}

export async function fetchGarmentTypes() {
  return runQuery(supabase.from("mfg_garment_types").select("id, name").order("name"));
}

export async function saveGarmentType(name, id = null) {
  const data = { name: name.trim() };
  if (!data.name) return { error: new Error("El nombre es obligatorio") };
  const result = await runQuery(id
    ? supabase.from("mfg_garment_types").update(data).eq("id", id).select().single()
    : supabase.from("mfg_garment_types").insert(data).select().single(), null);
  if (result.error?.code === "23505") return { error: new Error("Ya existe un tipo de prenda con ese nombre") };
  return result;
}

export async function deleteGarmentType(id) {
  const result = await runQuery(supabase.from("mfg_garment_types").delete().eq("id", id));
  if (result.error?.code === "23503") return { error: new Error("Este tipo de prenda está asignado a artículos. Reasígnalos antes de eliminarlo.") };
  return result;
}
